import React, { useCallback, useEffect } from "react";
import { useArrayContext } from "../../../state/ArrayContext";
import { SourceItem } from "../../../shared/models/source-item.model";
import { findRoot, getDOMParser, getFirstElementChild } from "../../../shared/utils/dom.utils";
import { useContent } from "../../../state/FileContext";

interface ContentStructureScannerProps {
    branchselector: string;
    labelselector: string;
}

/***
 * Array source that scans the content of the current page and returns the structure of the content
 * as a tree, which can be used to build a table of contents.
 * 
 * The tree is built by scanning the content for elements that match the branchselector and labelselector.  
 * The branchselector is used to find the branches of the tree, and the labelselector is used to find the labels
 * for the branches.
 * 
 * Each branch must be represented by a single element that matches the branchselector and that contains both
 * the label and the contents of the branch.
 * 
 * Params:
 *      branchselector: a css selector for the branches of the tree
 *      labelselector: a css selector for the labels of the tree
 */
export const ContentStructureScanner = React.memo(function ContentStructureScanner({ branchselector, labelselector }: ContentStructureScannerProps): React.ReactElement {
    const { setArray } = useArrayContext();
    const content = useContent();

    const findBranches: (root: Element | null, branchselector: string, labelselector: string) => Array<SourceItem> =
        useCallback((root: Element | null, branchselector: string, labelselector: string) => {
            const items: Array<SourceItem> = [];
            let element: Element | null = root;
            while (element) {
                if (element.matches(branchselector)) {
                    const label: Element | null = element.querySelector(labelselector);
                    if (label && label.textContent?.trim()) {
                        const item: SourceItem = {
                            value: label.textContent.trim(),
                            props: {
                                elements: {
                                    id: [label.closest("[id]")?.getAttribute("id") || ""],
                                }
                            },
                            position: items.length + 1,
                        };
                        item.children = findBranches(getFirstElementChild(element), branchselector, labelselector);
                        items.push(item);
                    }
                }
                else {
                    items.push(...findBranches(getFirstElementChild(element), branchselector, labelselector));
                }
                element = element.nextElementSibling;
            }
            return items;
        }, []);


    useEffect(() => {
        const doc: Document = getDOMParser().parseFromString(content || "", "text/html");
        const rootNode = findRoot(doc);
        if (rootNode) {
            const result: Array<SourceItem> = findBranches(rootNode, branchselector, labelselector);
            setArray(result);
        }
    }, [branchselector, labelselector, setArray, content, findBranches]);

    return <></>;
});